import { create } from 'zustand';
import api from '../lib/api';

export type SyncRunStatus = 'QUEUED' | 'RUNNING' | 'SUCCESS' | 'FAILED' | 'SKIPPED';

export interface SyncRun {
  id: string;
  syncName: string;
  status: SyncRunStatus;
  triggerType: 'scheduled' | 'manual';
  triggeredBy: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  exitCode: number | null;
  errorMessage: string | null;
  rowCounts: Record<string, number> | null;
  skipReason: string | null;
  createdAt: string;
  // Detail-only:
  logTail?: string | null;
  triggeredByUser?: { id: string; displayName: string | null; email: string } | null;
}

interface SyncStore {
  runs: SyncRun[];
  loading: boolean;
  error: string | null;
  fetch: (filters?: { syncName?: string; status?: SyncRunStatus; limit?: number }) => Promise<void>;
  trigger: (syncName: string) => Promise<void>;
  getDetail: (id: string) => Promise<SyncRun | null>;
}

export const useSyncStore = create<SyncStore>((set) => ({
  runs: [],
  loading: false,
  error: null,

  fetch: async (filters) => {
    set({ loading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (filters?.syncName) params.set('syncName', filters.syncName);
      if (filters?.status) params.set('status', filters.status);
      params.set('limit', String(filters?.limit ?? 30));
      const res = await api.get<{ runs: SyncRun[] }>(`/api/admin/syncs/runs?${params}`);
      set({ runs: res.data.runs, loading: false });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to load runs',
        loading: false,
      });
    }
  },

  trigger: async (syncName) => {
    await api.post('/api/admin/syncs/trigger', { syncName });
    // Caller refetches.
  },

  getDetail: async (id) => {
    try {
      const res = await api.get<{ run: SyncRun }>(`/api/admin/syncs/runs/${id}`);
      return res.data.run;
    } catch {
      return null;
    }
  },
}));
